import { type StateCreator } from 'zustand';
import { runTrackedAudioWork, swallowAudioWorkCutoff } from '../audioWorkBarrier';
import { captureUserContext } from '../configure/user-context-facade';
import { db } from '../db';
import {
  ledgerDatabaseExists,
  prepareWriteFenceForHydrate,
  resolvePipelineMode,
} from '../persistenceV2';
import { type SessionRecordingStore } from '../types';
import { logger } from './lib/logger';
import {
  drainLedgerBacklogV2,
  hydrateV2,
  type LedgerBacklogDrainSource,
} from './v2/hydrateV2';

export interface HydrationSlice {
  hydrated: boolean;
  hydrate: (sessionId: string) => Promise<void>;
}

// Hydration schedules its work fire-and-forget, so anything not already logged
// upstream is logged here rather than surfacing as an unhandled rejection. Name
// only, never the message: an upstream error can embed clinical content.
export const logHydrateWorkFailure =
  (event_type: string, recordingId: string) => (error: unknown) => {
    try {
      swallowAudioWorkCutoff(error);
    } catch (fatal) {
      logger.error({
        event_type,
        recording_id: recordingId,
        details: {
          error_name: fatal instanceof Error ? fatal.name : 'UnknownError',
        },
      });
    }
  };

export const createHydrationSlice: StateCreator<
  SessionRecordingStore,
  [],
  [],
  HydrationSlice
> = (set, get) => ({
  hydrated: false,

  // hydrate should only ever be called once on page load
  hydrate: async (sessionId) => {
    if (get().hydrated || !sessionId.length) return;
    set({ hydrated: true });
    // Confirm unfinished logout cleanup before either pipeline touches storage.
    // Recovery needs a readable store, so 'store-unusable' bails like
    // 'wipe-pending'; only a quiet start re-classifies a healed store.
    if ((await prepareWriteFenceForHydrate()) !== 'ready') {
      set({ hydrated: false });
      return;
    }
    let drainSource: LedgerBacklogDrainSource | null =
      (await resolvePipelineMode()) === 'ledger' ? 'active-ledger' : null;

    if (!drainSource) {
      // The flag controls new writes, not recovery reads. A page pinned to
      // legacy must still drain audio written by an earlier ledger-enabled load.
      try {
        drainSource = (await ledgerDatabaseExists()) ? 'legacy-rollback' : null;
      } catch (err) {
        logger.error({
          event_type: 'LEDGER_BACKLOG_PROBE_FAILED',
          details: { error: err instanceof Error ? err.message : String(err) },
        });
        set({ hydrated: false });
        return;
      }
    }

    if (drainSource) {
      const currentUserRefId = await captureUserContext.resolveCurrentUserRefId();
      if (!currentUserRefId) {
        // A slow login can time out the resolver — recoverable audio waits for
        // a later hydration, which must be observable, not a silent no-op.
        logger.warn({
          event_type: 'HYDRATE_USER_REF_ID_MISSING',
          details: { drainSource },
        });
        set({ hydrated: false });
        return;
      }
      if (drainSource === 'active-ledger') {
        await hydrateV2(set, get, currentUserRefId);
        return;
      }
      await drainLedgerBacklogV2(set, get, currentUserRefId, drainSource);
    }
    const sessions = await db.sessions.toArray();

    await db.deleteEmptySessions();

    sessions.forEach((session) => {
      session.recordings.forEach((recording) => {
        if (recording.backupRecording && !recording.backupRecording.isProcessed) {
          void runTrackedAudioWork(
            (context) => get().processBackup(recording.backupRecording!, context),
            'backup-upload'
          ).catch(logHydrateWorkFailure('HYDRATE_PROCESS_BACKUP_FAILED', recording.id));
        }
        recording.chunks.forEach((chunk) => {
          if (!chunk.isProcessed) {
            void runTrackedAudioWork(
              (context) =>
                get().processChunk(
                  chunk,
                  recording.useTranscriptionServiceV2 ?? false,
                  context
                ),
              'chunk-upload'
            ).catch(logHydrateWorkFailure('HYDRATE_PROCESS_CHUNK_FAILED', recording.id));
          }
        });
      });
    });
  },
});
